import { BrandIcon } from "./BrandIcon";
import { Icon } from "./shared";

const stages = [
  "Maya is reviewing the delivery brief.",
  "Maya left a comment. Claude has the same context.",
  "Claude acknowledged the correction and is updating the plan.",
  "Claude corrected the delivery date in the document.",
  "Brief updated. The whole team has the same next step.",
];
export const sharedBriefLastStep = stages.length - 1;

export function SharedBrief({ step }: { step: number }) {
  return (
    <div className="g-brief g-shared-brief" data-step={step}>
      <div className="g-brief-top">
        <span>
          <Icon name="page" /> Fern & Field / Juniper Café
        </span>
        <span className="g-presence">
          <i>M</i>
          <i>C</i> 2 here
        </span>
      </div>
      <div className="g-collab-layout">
        <div
          className="g-collab-document"
          role="region"
          aria-label="Shared customer document"
        >
          <span className="g-document-eyebrow">
            FIRST DELIVERY / SHARED BRIEF
          </span>
          <h3>Plants for opening day.</h3>
          <p className="g-collab-intro">
            Juniper Café is opening its doors. <br />
            Let’s make it feel alive.
          </p>
          <div className="g-document-divider" />
          <span className="g-field-label">The plan</span>
          <p className={`g-collab-passage ${step >= 1 ? "is-commented" : ""}`}>
            Deliver Juniper Café’s first plants{" "}
            {step >= 3 ? (
              <span className="g-edited-date">
                <del>next week</del>
                <ins>
                  this Friday
                  <span className="g-edit-caret" aria-hidden="true" />
                </ins>
              </span>
            ) : (
              <span>next week</span>
            )}
            .
            {step >= 1 && (
              <span
                className="g-margin-comment"
                aria-label="One comment on this passage"
              >
                <Icon name="chat" />
                <small>1</small>
              </span>
            )}
          </p>
          <div className="g-document-next-step">
            <span className="g-field-label">Next step</span>
            {step >= 4 ? (
              <div className="g-inserted-task">
                <span className="g-task-outline" aria-hidden="true" />
                <div>
                  <strong>Pack pet-safe plants</strong>
                  <p>
                    Choose calatheas from our pet-safe playbook. This café
                    welcomes dogs.
                  </p>
                  <span className="g-task-assignee">
                    <span className="g-comment-avatar">M</span> Maya{" "}
                    <span>Before Friday</span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="g-unassigned-task">
                <Icon name="plus" />
                <span>Agree on the next step</span>
              </div>
            )}
          </div>
          <div className="g-document-credit">
            {step >= 3 ? (
              <>
                <BrandIcon name="claude" />
                <span>
                  Edited by Claude
                  {step >= 4 ? " · shared with everyone" : " · updating…"}
                </span>
              </>
            ) : (
              <>
                <Icon name="people" />
                <span>One brief for people and agents.</span>
              </>
            )}
          </div>
        </div>
        <aside className="g-comment-sidebar" aria-label="Document comments">
          <div className="g-sidebar-heading">
            <Icon name="chat" />
            <strong>Comments</strong>
            <span>{step >= 2 ? 2 : step >= 1 ? 1 : 0}</span>
          </div>
          <div className="g-sidebar-thread">
            {step >= 1 ? (
              <div className="g-sidebar-comment g-maya-comment">
                <div className="g-sidebar-author">
                  <span className="g-comment-avatar">M</span>
                  <strong>
                    Maya<small>Teammate</small>
                  </strong>
                  <span>now</span>
                </div>
                <blockquote>“first plants next week”</blockquote>
                <p>
                  It’s this Friday, actually. The café welcomes dogs—can we make
                  the plants pet-safe,{" "}
                  <span className="g-agent-mention">@Claude</span>?
                </p>
              </div>
            ) : (
              <div className="g-comment-drafting">
                <span className="g-comment-avatar">M</span>
                <div>
                  <strong>Maya is adding a comment</strong>
                  <span className="g-draft-strokes" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                </div>
              </div>
            )}
            {step >= 2 ? (
              <div className="g-sidebar-comment g-claude-comment">
                <div className="g-sidebar-author">
                  <span className="g-comment-avatar">
                    <BrandIcon name="claude" />
                  </span>
                  <strong>
                    Claude<small>Agent</small>
                  </strong>
                  <span>now</span>
                </div>
                <p>
                  You’re right. I’ll correct the date and add calatheas from our
                  pet-safe playbook to Maya’s packing list.
                </p>
                <span
                  className={`g-agent-edit-status ${step >= 4 ? "is-saved" : ""}`}
                >
                  <Icon name={step >= 4 ? "check" : "page"} />
                  {step >= 4
                    ? "Updated the shared brief"
                    : "Editing the document…"}
                </span>
              </div>
            ) : step === 1 ? (
              <div className="g-sidebar-typing">
                <BrandIcon name="claude" />
                <span>
                  Claude is reading
                  <span className="g-typing-dots">
                    <i />
                    <i />
                    <i />
                  </span>
                </span>
              </div>
            ) : null}
          </div>
          <div className="g-sidebar-note">
            <Icon name="branch" /> A comment becomes a better plan.
          </div>
        </aside>
      </div>
      <div className="g-brief-footer" role="status">
        <span className="g-live-dot" />
        {stages[step]}
        <span
          className="g-scene-progress"
          aria-label={`Collaboration step ${step + 1} of ${stages.length}`}
        >
          {stages.map((_, index) => (
            <i key={index} className={step >= index ? "is-complete" : ""} />
          ))}
        </span>
      </div>
    </div>
  );
}
